/**
 * ExchangeRateModal — 환율 흐름 차트 팝업 (기간 Segmented: 1M/3M/6M/1Y)
 * 데이터: GET /v1/portfolio/exchange-rate/history?days=N (usdkrw_rates, 주말 제외 자동)
 * 쿼리키 ["brief","fx","history",days] — 모달을 닫았다 열어도 캐시 유지.
 */
import { useState } from "react";
import { Modal, Segmented, Spin, theme } from "antd";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import ReactEcharts from "src/lib/EChart";
import { briefApi, ExchangeRateHistoryVO } from "src/api/brief";
import { tooltipTheme, useChartTheme } from "src/components/charts/chartTheme";
import { useThemeStore } from "src/stores/ThemeStore";
import { useScreenStore } from "src/stores/screenStore";
import { useOverlayBackKey } from "src/hooks/useOverlayBackKey";

const PERIODS = [
  { label: "1M", value: 30 },
  { label: "3M", value: 90 },
  { label: "6M", value: 180 },
  { label: "YTD", value: dayjs().diff(dayjs().startOf("year"), "day") },
  { label: "1Y", value: 365 },
] as const;

interface ExchangeRateModalProps {
  open: boolean;
  onClose: () => void;
  currentRate?: number;
}

const ExchangeRateModal = ({ open, onClose, currentRate }: ExchangeRateModalProps) => {
  const { token } = theme.useToken();
  const isDark = useThemeStore((s) => s.isDarkMode);
  const chartTheme = useChartTheme();
  const screens = useScreenStore((s) => s.screens);
  const [days, setDays] = useState<number>(90);

  useOverlayBackKey(open, onClose);

  const { data, isLoading } = useQuery({
    queryKey: ["brief", "fx", "history", days],
    queryFn: () => briefApi.getExchangeRateHistory(days),
    enabled: open,
    staleTime: 10 * 60_000,
    placeholderData: (prev: ExchangeRateHistoryVO[] | undefined) => prev,
  });

  const option = useMemoOption(data, isDark, token.colorTextSecondary);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span>환율 흐름 (USD/KRW)</span>
          {currentRate != null && (
            <span style={{ fontSize: 13, color: token.colorTextSecondary, fontWeight: 400 }}>
              {currentRate.toFixed(1)}원
            </span>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={screens.md ? 680 : "94vw"}
      styles={{ body: { paddingTop: 12 } }}
    >
      <Segmented
        size="small"
        value={days}
        onChange={(v) => setDays(v as number)}
        options={PERIODS.map((p) => ({ label: p.label, value: p.value }))}
        style={{ marginBottom: 8 }}
      />
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : (
        <ReactEcharts
          option={option}
          theme={chartTheme}
          notMerge
          style={{ height: screens.sm ? 320 : 240, width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      )}
      <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 4 }}>
        출처: usdkrw_rates (영업일 기준)
      </div>
    </Modal>
  );
};

/** 차트 옵션 — 고/저점은 현재 환율(마지막 값) 대비 % 마크 (StockCandleChart e140044 패턴 승계) */
const fmtRate = (v: number) => v.toFixed(1);

function useMemoOption(
  data: ExchangeRateHistoryVO[] | undefined,
  isDark: boolean,
  labelColor: string,
) {
  const rows = data ?? [];
  const rates = rows.map((r) => r.rate);
  const last = rates.length ? rates[rates.length - 1] : 0;

  // 기간 내 최고/최저 인덱스 (현재 환율 기준 증감률)
  let hiIdx = 0;
  let loIdx = 0;
  rates.forEach((v, i) => {
    if (v > rates[hiIdx]) hiIdx = i;
    if (v < rates[loIdx]) loIdx = i;
  });
  const hiPct = last ? ((rates[hiIdx] - last) / last) * 100 : 0;
  const loPct = last ? ((rates[loIdx] - last) / last) * 100 : 0;

  // 환율 상승=원화 약세 → 빨강 / 하락=원화 강세 → 파랑 (서비스 색상 규칙)
  const upColor = "#ff4d4f";
  const downColor = "#1890ff";

  return {
    grid: { top: 24, right: 14, bottom: 24, left: 56 },
    tooltip: {
      trigger: "axis" as const,
      ...tooltipTheme(isDark),
      valueFormatter: (v: number) => `${fmtRate(v)}원`,
    },
    xAxis: {
      type: "category" as const,
      data: rows.map((r) => r.date),
      axisLabel: {
        color: labelColor,
        fontSize: 11,
        formatter: (v: string) => v.slice(5).replace("-", "/"),
      },
      axisLine: { lineStyle: { color: isDark ? "#444" : "#ddd" } },
    },
    yAxis: {
      type: "value" as const,
      scale: true,
      axisLabel: { color: labelColor, fontSize: 11, formatter: (v: number) => fmtRate(v) },
      splitLine: { lineStyle: { color: isDark ? "#333" : "#eee" } },
    },
    series: [
      {
        type: "line" as const,
        data: rates,
        showSymbol: false,
        smooth: 0.2,
        lineStyle: { width: 1.8 },
        areaStyle: { opacity: 0.12 },
        markPoint: {
          symbol: "circle",
          symbolSize: 5,
          animation: false,
          data:
            rows.length > 1
              ? [
                  {
                    coord: [hiIdx, rates[hiIdx]],
                    value: `고 ${hiPct >= 0 ? "+" : ""}${hiPct.toFixed(1)}%`,
                    itemStyle: { color: upColor },
                    label: { show: true, position: "top", fontSize: 10, fontWeight: 600, color: upColor, distance: 5 },
                  },
                  {
                    coord: [loIdx, rates[loIdx]],
                    value: `저 ${loPct >= 0 ? "+" : ""}${loPct.toFixed(1)}%`,
                    itemStyle: { color: downColor },
                    label: { show: true, position: "bottom", fontSize: 10, fontWeight: 600, color: downColor, distance: 5 },
                  },
                ]
              : [],
        },
      },
    ],
  };
}

export default ExchangeRateModal;
